import { useState, useMemo } from 'react';
import type { DailyLeadByAgent, MonthlyChartByAgent } from '../services/leadStatsApi';

interface LeadsDailyChartProps {
  dailyData: DailyLeadByAgent[];
  monthlyData: MonthlyChartByAgent[];
  selectedAgent: string | null;
}

const DAY_LABELS: Record<string, string> = {
  Mon: 'L', Tue: 'M', Wed: 'X', Thu: 'J', Fri: 'V', Sat: 'S', Sun: 'D',
};
const MONTH_LABELS: Record<string, string> = {
  Jan: 'Ene', Feb: 'Feb', Mar: 'Mar', Apr: 'Abr', May: 'May', Jun: 'Jun',
  Jul: 'Jul', Aug: 'Ago', Sep: 'Sep', Oct: 'Oct', Nov: 'Nov', Dec: 'Dic',
};

const GREEN_SHADES = [
  '#00FF7A', '#00E86E', '#00D162', '#00BA56', '#00A34A',
  '#008C3E', '#007532', '#005E26', '#00471A', '#00300E',
];

type ViewMode = '7d' | '6m';

export default function LeadsDailyChart({ dailyData, monthlyData, selectedAgent }: LeadsDailyChartProps) {
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [view, setView] = useState<ViewMode>('7d');

  const rawData = view === '7d' ? dailyData : monthlyData;

  const timeKeys = useMemo(() => {
    const key = view === '7d' ? 'date' : 'month';
    return Array.from(new Set(rawData.map((d: any) => d[key]))).sort();
  }, [rawData, view]);

  const agentColorMap = useMemo(() => {
    const totals = new Map<string, number>();
    for (const d of rawData) {
      if (d.count > 0) totals.set(d.agent_name, (totals.get(d.agent_name) || 0) + d.count);
    }
    const sorted = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    const map = new Map<string, string>();
    sorted.forEach(([name], i) => map.set(name, GREEN_SHADES[i % GREEN_SHADES.length]));
    return map;
  }, [rawData]);

  const bars = useMemo(() => {
    const keyField = view === '7d' ? 'date' : 'month';
    const labelField = view === '7d' ? 'day_name' : 'month_name';

    return timeKeys.map(tk => {
      const entries = rawData.filter((d: any) => d[keyField] === tk);
      const label = (entries[0] as any)?.[labelField] || '';

      if (selectedAgent) {
        const entry = entries.find(d => d.agent_id === selectedAgent);
        return { key: tk, label, total: entry?.count || 0, segments: [] as { name: string; count: number; color: string }[] };
      }

      const segments = entries
        .filter(d => d.count > 0)
        .map(d => ({ name: d.agent_name, count: d.count, color: agentColorMap.get(d.agent_name) || '#ccc' }))
        .sort((a, b) => {
          const keys = Array.from(agentColorMap.keys());
          return keys.indexOf(a.name) - keys.indexOf(b.name);
        });

      return { key: tk, label, total: segments.reduce((s, x) => s + x.count, 0), segments };
    });
  }, [timeKeys, rawData, selectedAgent, agentColorMap, view]);

  const maxVal = Math.max(...bars.map(b => b.total), 1);
  const total = bars.reduce((s, b) => s + b.total, 0);
  const isAll = !selectedAgent;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Leads</h3>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">{total.toLocaleString()}</p>
        </div>
        <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setView('7d')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
              view === '7d' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            7 dias
          </button>
          <button
            onClick={() => setView('6m')}
            className={`px-3 py-1 text-[11px] font-medium rounded-md transition-all ${
              view === '6m' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            6 meses
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="flex items-end gap-2 flex-1 min-h-[180px]">
        {bars.map((bar, i) => {
          const pct = (bar.total / maxVal) * 100;
          const isHovered = hoveredIdx === i;
          const displayLabel = view === '7d'
            ? (DAY_LABELS[bar.label] || bar.label)
            : (MONTH_LABELS[bar.label] || bar.label);

          const tooltipLabel = view === '7d'
            ? new Date(bar.key + 'T12:00:00').toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short' })
            : new Date(bar.key + '-15').toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

          return (
            <div
              key={bar.key}
              className="flex-1 flex flex-col items-center justify-end h-full relative"
              onMouseEnter={() => setHoveredIdx(i)}
              onMouseLeave={() => setHoveredIdx(null)}
            >
              {/* Tooltip */}
              {isHovered && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-[#1A4D2E] text-white text-[11px] rounded-xl px-3 py-2 whitespace-nowrap z-10 shadow-xl min-w-[120px]">
                  <p className="font-medium capitalize mb-0.5">{tooltipLabel}</p>
                  {isAll && bar.segments.length > 1 ? (
                    <div className="space-y-px max-h-36 overflow-y-auto">
                      {bar.segments.slice(0, 8).map((seg, j) => (
                        <div key={j} className="flex items-center justify-between gap-3">
                          <span className="flex items-center gap-1">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: seg.color }} />
                            <span className="truncate max-w-[100px]">{seg.name}</span>
                          </span>
                          <span className="font-semibold tabular-nums">{seg.count}</span>
                        </div>
                      ))}
                      {bar.segments.length > 8 && (
                        <p className="text-emerald-200 text-[10px]">+{bar.segments.length - 8} mas</p>
                      )}
                      <div className="border-t border-emerald-600 mt-1 pt-1 flex justify-between font-semibold">
                        <span>Total</span><span>{bar.total}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="font-semibold">{bar.total} leads</p>
                  )}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-full w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent border-t-[#1A4D2E]" />
                </div>
              )}

              {/* Bar */}
              <div
                className="w-full max-w-[44px] rounded-xl overflow-hidden flex flex-col-reverse cursor-pointer transition-all duration-150"
                style={{
                  height: `${Math.max(pct, bar.total > 0 ? 3 : 0)}%`,
                  opacity: hoveredIdx !== null && !isHovered ? 0.35 : 1,
                  transform: isHovered ? 'scaleY(1.02)' : 'scaleY(1)',
                  transformOrigin: 'bottom',
                }}
              >
                {isAll && bar.segments.length > 1 ? (
                  bar.segments.map((seg, j) => (
                    <div
                      key={j}
                      style={{
                        height: `${(seg.count / bar.total) * 100}%`,
                        backgroundColor: seg.color,
                        minHeight: '1px',
                      }}
                    />
                  ))
                ) : (
                  <div className="w-full h-full bg-[#00FF7A]" />
                )}
              </div>

              <span className={`text-[11px] mt-2 transition-colors ${isHovered ? 'text-gray-900 font-semibold' : 'text-gray-400'}`}>
                {displayLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
